import { FunctionComponent, useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import { BubbleMenu } from '@tiptap/react/menus';
import { EditorContent, useEditor } from '@tiptap/react';
import {
  checkSelectionHasFormat,
  getSelectedHTML,
  getSelectedTextColor,
  sanitiseEditorTipTap,
} from '../../../kids/utils/editor.utils';
import { LuBold, LuItalic, LuStrikethrough, LuUnderline } from 'react-icons/lu';
import { IoIosClose } from 'react-icons/io';
import { cn } from '../../../../../../utilities/cn';
import type { PdfElement } from '../../types/KidPdfGeneratorV2.types';
import type { Editor } from '@tiptap/react';

/* -------------------------------------------------------------------------- */
/*                            Toolbar Button Component                         */
/* -------------------------------------------------------------------------- */
interface ToolbarButtonProps {
  icon: React.ReactNode;
  isActive?: boolean;
  onClick: () => void;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
  icon,
  isActive,
  onClick,
}) => (
  <button
    type="button"
    onMouseDown={(e) => {
      e.preventDefault();
      onClick();
    }}
    className={cn(
      'flex h-8 w-8 items-center justify-center rounded-md border border-transparent transition-all hover:border-gray-200 hover:bg-gray-100',
      {
        'border-blue-300 bg-blue-50 text-blue-700 shadow-sm': isActive,
      },
    )}
  >
    {icon}
  </button>
);

/* -------------------------------------------------------------------------- */
/*                          Text Editor Bubble Menu Component                  */
/* -------------------------------------------------------------------------- */
interface EditorBubbleMenuProps {
  editor: Editor;
  selectedHTML: string;
  index: number | string;
}

const EditorBubbleMenu: FunctionComponent<EditorBubbleMenuProps> = ({
  editor,
  selectedHTML,
  index,
}) => {


  return (
    <BubbleMenu
      editor={editor}
      className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-2 shadow-lg"
    >
      <ToolbarButton
        icon={<LuBold size={18} />}
        isActive={checkSelectionHasFormat(selectedHTML, 'bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      />
      <ToolbarButton
        icon={<LuItalic size={18} />}
        isActive={checkSelectionHasFormat(selectedHTML, 'italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      />
      <ToolbarButton
        icon={<LuUnderline size={18} />}
        isActive={checkSelectionHasFormat(selectedHTML, 'underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      />
      <ToolbarButton
        icon={<LuStrikethrough size={18} />}
        isActive={checkSelectionHasFormat(selectedHTML, 'strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      />

      {/* Divider */}
      <div className="mx-2 h-6 w-px bg-gray-200" />

      {/* Text Color */}
      <div className="flex items-center gap-1.5">
        <label
          className="text-xs font-medium text-gray-600"
          htmlFor={`textColor-${index}`}
        >
          Color
        </label>
        <div className="relative">
          <input
            id={`textColor-${index}`}
            type="color"
            className="h-7 w-7 cursor-pointer rounded border border-gray-300 p-0 shadow-sm transition-shadow hover:shadow-md"
            value={(() => {
              let color = getSelectedTextColor(selectedHTML);

              // Convert rgb(a) to hex if necessary
              if (color.startsWith('rgb')) {
                const rgb = color.match(/\d+/g)?.map(Number) || [0, 0, 0];
                color =
                  '#' +
                  rgb
                    .slice(0, 3)
                    .map((v) => v.toString(16).padStart(2, '0'))
                    .join('');
              }

              return color;
            })()}
            onChange={(e) => {
              const value = e.target.value || '#000000';
              editor.chain().focus().setColor(value).run();
            }}
          />
        </div>
      </div>

      {/* Highlight Color */}
      <div className="ml-2 flex items-center gap-1.5">
        <label
          className="text-xs font-medium text-gray-600"
          htmlFor={`highlightColor-${index}`}
        >
          Highlight
        </label>
        <div className="flex items-center gap-1">
          <input
            id={`highlightColor-${index}`}
            type="color"
            className="h-7 w-7 cursor-pointer rounded border border-gray-300 p-0 shadow-sm transition-shadow hover:shadow-md"
            value={(() => {
              const match = selectedHTML?.match(
                /<mark[^>]*data-color=["'](#[0-9A-Fa-f]{3,6})["']/i,
              );
              const markupColor = match ? match[1] : null;
              return markupColor || '#ffff00';
            })()}
            onChange={(e) => {
              const value = e.target.value || '#ffff00';
              editor.chain().focus().setHighlight({ color: value }).run();
            }}
          />
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              editor.chain().focus().unsetHighlight().run();
            }}
            disabled={!/\<mark[^>]*\>/i.test(selectedHTML || '')}
            aria-label="Remove highlight from selection"
            title="Remove highlight from selection only (keeps other formatting)"
            className="flex h-7 w-7 items-center justify-center rounded-md border border-gray-300 bg-gray-50 text-gray-500 transition-all hover:border-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-gray-50 disabled:hover:text-gray-500"
          >
            <IoIosClose size={16} />
          </button>
        </div>
      </div>
    </BubbleMenu>
  );
};

/* -------------------------------------------------------------------------- */
/*                          Main Text Input Component                          */
/* -------------------------------------------------------------------------- */
interface TextInputTipTapProps {
  field: PdfElement;
  index: number;
  allowStyling?: boolean; // If false, only allows text editing without any styling
  customClassName?: string; // Optional custom className for the editor content
}

const TextInputTipTap: FunctionComponent<TextInputTipTapProps> = ({
  field,
  index = '',
  allowStyling = true, // Default to true for backward compatibility
  customClassName,
}) => {

  // Track selected HTML to check formatting
  const [selectedHTML, setSelectedHTML] = useState<string>('');

  const content = field.content as string;
  const { control, setValue, trigger } = useFormContext();
  const formatOptions = useWatch({
    name: `elements.${index}.format_options`,
    control,
  });

  // Convert old HTML format to TipTap-compatible format
  // Converts <font color="..."> to <span style="color: ...">
  // Converts <font backColor="..."> to <mark data-color="...">
  const convertLegacyHTML = (html: string): string => {
    if (!html) return html;

    let converted = html;

    // First, convert <font backColor="..."> to <mark data-color="...">
    converted = converted.replace(
      /<font\s+([^>]*\s+)?backColor=["']([^"']+)["']([^>]*)>/gi,
      (match, before, color, after) => {
        const attrs = ((before || '') + (after || ''))
          .replace(/\s*backColor=["'][^"']+["']/gi, '')
          .trim();
        return `<mark data-color="${color}"${attrs ? ` ${attrs}` : ''}>`;
      },
    );

    // Convert <font color="..."> to <span style="color: ...">
    converted = converted.replace(
      /<font\s+([^>]*\s+)?color=["']([^"']+)["']([^>]*)>/gi,
      (match, before, color, after) => {
        const attrs = ((before || '') + (after || ''))
          .replace(/\s*color=["'][^"']+["']/gi, '')
          .trim();

        // Handle style attribute
        let styleAttr = `style="color: ${color}"`;
        if (attrs.includes('style=')) {
          const styleMatch = attrs.match(/style=["']([^"']*)["']/i);
          if (styleMatch && !styleMatch[1].includes('color:')) {
            styleAttr = `style="${styleMatch[1]}; color: ${color}"`;
          } else if (styleMatch) {
            styleAttr = styleMatch[0];
          }
        }

        const otherAttrs = attrs.replace(/style=["'][^"']*["']/gi, '').trim();
        return `<span ${styleAttr}${otherAttrs ? ` ${otherAttrs}` : ''}>`;
      },
    );

    // Convert remaining <font> tags to <span>
    converted = converted.replace(/<font([^>]*)>/gi, '<span$1>');

    // Convert closing tags - try to match with the nearest opening tag
    // For simplicity, we'll replace all </font> and let TipTap's parser handle structure
    converted = converted.replace(/<\/font>/gi, (match, offset, string) => {
      const before = string.substring(0, offset);
      // Find the last unclosed opening tag before this closing tag
      const lastMark = before.lastIndexOf('<mark');
      const lastSpan = before.lastIndexOf('<span');
      const lastFont = before.lastIndexOf('<font');

      // Count unclosed tags
      const markCount =
        (before.match(/<mark[^>]*>/gi) || []).length -
        (before.match(/<\/mark>/gi) || []).length;
      const spanCount =
        (before.match(/<span[^>]*>/gi) || []).length -
        (before.match(/<\/span>/gi) || []).length;

      // If we have an unclosed mark tag, close as mark
      if (markCount > spanCount && lastMark > lastFont) {
        return '</mark>';
      }
      return '</span>';
    });

    return converted;
  };

  // Extract plain text from HTML (for non-styling mode)
  const extractPlainText = (html: string): string => {
    if (!html) return '';

    return html.replace(/<[^>]*>/g, '').trim();
  };

  // Convert plain text to simple paragraph HTML
  const textToHTML = (text: string): string => {
    if (!text) return '<p></p>';
    return `<p>${text}</p>`;
  };

  const editor = useEditor({
    extensions: allowStyling
      ? [
        StarterKit,
        TextStyle,
        Color,
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        Highlight.configure({ multicolor: true }),
      ]
      : [
        StarterKit.configure({
          // Disable all formatting options when styling is not allowed
          bold: false,
          italic: false,
          strike: false,
          code: false,
          heading: false,
          blockquote: false,
          horizontalRule: false,
          hardBreak: false,
        }),
      ],
    content: allowStyling
      ? convertLegacyHTML(content || '')
      : textToHTML(extractPlainText(content || '')),
    onUpdate: ({ editor }) => {
      if (allowStyling) {
        const html = editor.getHTML();
        // Clean malformed HTML before sanitizing
        const sanitised = sanitiseEditorTipTap(html);
        setValue(`elements.${index}.content`, sanitised);
      } else {
        // Strip all formatting - only keep plain text
        const plainText = editor.getText().trim();
        const sanitised = textToHTML(plainText);
        setValue(`elements.${index}.content`, sanitised);
      }
      trigger(`elements.${index}`);
    },
    editorProps: {
      attributes: {
        class: customClassName || 'min-h-24 outline-none p-2 text-gray-900',
      },
      handleDOMEvents: {
        keydown: (_view, event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            return true; // stop Enter from inserting a newline or hard break
          }
          return false;
        },
      },
    },
  });

  // Apply block-level text alignment from format_options
  useEffect(() => {
    if (editor && formatOptions?.textAlign && allowStyling) {
      const alignment = formatOptions.textAlign as 'left' | 'center' | 'right';
      // Apply alignment via CSS to the editor container (affects all content)
      const editorElement = editor.view.dom as HTMLElement;
      if (editorElement) {
        editorElement.style.textAlign = alignment;
      }
      // Also apply to all paragraphs and headings via TipTap commands (only if TextAlign extension is enabled)
      if (editor.commands.setTextAlign) {
        editor.commands.setTextAlign(alignment);
      }
    }
  }, [editor, formatOptions?.textAlign, allowStyling]);

  // Apply block-level font size from format_options
  useEffect(() => {
    if (editor && formatOptions?.fontSize) {
      const fontSize = formatOptions.fontSize;
      // Apply font size via CSS to the editor container (affects all content)
      const editorElement = editor.view.dom as HTMLElement;
      if (editorElement) {
        editorElement.style.fontSize = `${fontSize}px`;
      }
    }
  }, [editor, formatOptions?.fontSize]);

  useEffect(() => {
    if (!editor || !allowStyling) return;

    const updateSelection = () => {
      const html = getSelectedHTML(editor);
      setSelectedHTML(html);
    };

    // Update on selection change
    editor.on('selectionUpdate', updateSelection);
    editor.on('update', updateSelection);

    return () => {
      editor.off('selectionUpdate', updateSelection);
      editor.off('update', updateSelection);
    };
  }, [editor, allowStyling]);

  // Update editor content when field content changes (for non-styling mode)
  useEffect(() => {
    if (!editor || allowStyling) return;

    const plainText = extractPlainText(content || '');
    const currentText = editor.getText().trim();
    if (plainText !== currentText) {
      editor.commands.setContent(textToHTML(plainText));
    }
  }, [content, editor, allowStyling]);



  return (
    <Controller
      name={`elements.${index}.content`}
      control={control}
      render={({ field, fieldState }) => (
        <div className="relative">
          {/* --- Bubble Menu --- */}
          {editor && allowStyling && (
            <EditorBubbleMenu
              editor={editor}
              selectedHTML={selectedHTML}
              index={index}
            />
          )}

          {/* --- Editable Area --- */}
          <EditorContent
            editor={editor}
            onBlur={() => trigger(`elements.${index}.content`)}
            className={cn(
              customClassName
                ? 'w-full cursor-text rounded border bg-white outline-none'
                : 'min-h-24 w-full cursor-text rounded border bg-white p-2 text-gray-900 outline-none',
              fieldState.error
                ? 'border-red-500 hover:border-red-600 focus:border-red-500'
                : 'border-gray-300 hover:border-blue-500 focus:border-blue-500',
            )}
            style={{
              textAlign: formatOptions?.textAlign || 'left',
            }}
          />
          {fieldState.error && (
            <p className="mt-1 text-xs text-red-600">
              {fieldState.error.message}
            </p>
          )}

        </div>
      )}
    />
  );
};

export default TextInputTipTap;
